import { Recipe } from '../types/mealPlan';
import { formatQuantity } from './formatQuantity';
import { formatTime } from './formatTime';
import { formatIngredientName } from './formatIngredient';

export const printRecipe = (recipe: Recipe) => {
  if (!recipe) return;

  // Generate HTML content
  let htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${recipe.name}</title>
        <meta charset="utf-8">
        <style>
          @page {
            margin: 0.75in;
          }
          body {
            font-family: Arial, sans-serif;
            line-height: 1.5;
            color: #000;
            background: white;
            font-size: 11pt;
          }
          .header {
            text-align: center;
            margin-bottom: 25px;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
          }
          .header h1 {
            margin: 0 0 8px 0;
            font-size: 20pt;
            font-weight: bold;
          }
          .header .description {
            font-size: 11pt;
            color: #666;
            margin-top: 8px;
            font-style: italic;
          }
          .metadata {
            display: flex;
            justify-content: center;
            gap: 20px;
            margin-bottom: 25px;
            padding: 15px;
            background: #f8f8f8;
            border-radius: 4px;
            flex-wrap: wrap;
          }
          .metadata-item {
            text-align: center;
            min-width: 80px;
          }
          .metadata-label {
            font-size: 8pt;
            text-transform: uppercase;
            color: #666;
            font-weight: 600;
            margin-bottom: 4px;
            white-space: nowrap;
          }
          .metadata-value {
            font-size: 10pt;
            font-weight: 500;
          }
          .badges {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 25px;
            justify-content: center;
          }
          .badge {
            display: inline-block;
            padding: 4px 10px;
            background: #e9ecef;
            border-radius: 4px;
            font-size: 9pt;
            font-weight: 500;
          }
          .content {
            margin-bottom: 25px;
          }
          .section {
            margin-bottom: 25px;
          }
          .section h2 {
            font-size: 14pt;
            font-weight: bold;
            margin: 0 0 12px 0;
            padding-bottom: 6px;
            border-bottom: 1px solid #999;
          }
          .ingredients-list {
            list-style: none;
            padding: 0;
            margin: 0;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0 20px;
          }
          .ingredient-item {
            padding: 6px 0;
            border-bottom: 1px solid #eee;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .ingredient-quantity {
            font-weight: 600;
          }
          .ingredient-preparation {
            color: #666;
            font-style: italic;
          }
          .ingredient-optional {
            color: #666;
            font-size: 9pt;
          }
          .instructions-list {
            list-style: none;
            padding: 0;
            margin: 0;
            counter-reset: step-counter;
          }
          .instruction-item {
            padding: 12px 0;
            page-break-inside: avoid;
            break-inside: avoid;
            counter-increment: step-counter;
            display: flex;
            gap: 12px;
          }
          .instruction-item::before {
            content: counter(step-counter);
            flex-shrink: 0;
            width: 28px;
            height: 28px;
            background: #333;
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 12pt;
          }
          .instruction-text {
            flex: 1;
            padding-top: 4px;
          }
          .notes {
            margin-top: 25px;
            padding: 15px;
            background: #fffdf0;
            border-left: 3px solid #ffc107;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .notes h3 {
            font-size: 12pt;
            font-weight: bold;
            margin: 0 0 8px 0;
          }
          .notes p {
            margin: 0;
            white-space: pre-wrap;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 9pt;
            color: #666;
            border-top: 1px solid #eee;
            padding-top: 15px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${recipe.name}</h1>
          ${recipe.cuisine ? `<div style="font-size: 11pt; color: #666; font-weight: 600;">${recipe.cuisine}</div>` : ''}
          ${recipe.description ? `<div class="description">${recipe.description}</div>` : ''}
        </div>

        <div class="metadata">
          <div class="metadata-item">
            <div class="metadata-label">Prep time</div>
            <div class="metadata-value">${formatTime(recipe.prepTimeMinutes)}</div>
          </div>
          <div class="metadata-item">
            <div class="metadata-label">Cook time</div>
            <div class="metadata-value">${formatTime(recipe.cookTimeMinutes)}</div>
          </div>
          <div class="metadata-item">
            <div class="metadata-label">Total time</div>
            <div class="metadata-value">${formatTime(recipe.totalTimeMinutes)}</div>
          </div>
          <div class="metadata-item">
            <div class="metadata-label">Servings</div>
            <div class="metadata-value">${recipe.servings}</div>
          </div>
          ${recipe.difficulty ? `
          <div class="metadata-item">
            <div class="metadata-label">Difficulty</div>
            <div class="metadata-value">${recipe.difficulty.charAt(0).toUpperCase() + recipe.difficulty.slice(1)}</div>
          </div>
          ` : ''}
        </div>
  `;

  // Add badges for dietary restrictions
  if (recipe.dietaryRestrictions && recipe.dietaryRestrictions.length > 0) {
    htmlContent += '<div class="badges">';

    recipe.dietaryRestrictions.forEach(restriction => {
      htmlContent += `<span class="badge">${restriction}</span>`;
    });

    htmlContent += '</div>';
  }

  // Ingredients section
  htmlContent += `
    <div class="section">
      <h2>Ingredients</h2>
      <ul class="ingredients-list">
  `;

  recipe.ingredients?.forEach(ingredient => {
    const formattedQuantity = formatQuantity(ingredient.quantity, ingredient.unit);
    const name = formatIngredientName(ingredient);

    htmlContent += `
      <li class="ingredient-item">
        <span class="ingredient-quantity">${formattedQuantity}</span> ${name}
        ${ingredient.preparation ? `<span class="ingredient-preparation"> — ${ingredient.preparation}</span>` : ''}
        ${ingredient.optional ? '<span class="ingredient-optional"> (optional)</span>' : ''}
      </li>
    `;
  });

  htmlContent += '</ul></div>';

  // Instructions section
  htmlContent += `
    <div class="section">
      <h2>Instructions</h2>
      <ul class="instructions-list">
  `;

  recipe.instructions?.forEach(instruction => {
    const instructionText = instruction.text;

    htmlContent += `
      <li class="instruction-item">
        <div class="instruction-text">
          ${instructionText}
        </div>
      </li>
    `;
  });

  htmlContent += '</ul></div>';

  // Notes section
  if (recipe.notes) {
    htmlContent += `
      <div class="notes">
        <h3>Notes</h3>
        <p>${recipe.notes}</p>
      </div>
    `;
  }

  // Footer
  htmlContent += `
        <div class="footer">
          Generated by Meals You Love • ${new Date().toLocaleDateString()}
        </div>
      </body>
    </html>
  `;

  // Create a blob and object URL for the HTML content
  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);

  // Open print window with the blob URL
  const printWindow = window.open(url, '_blank');
  if (printWindow) {
    // Wait for content to load, then print
    printWindow.addEventListener('load', () => {
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        // Clean up the object URL after printing
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);
      }, 250);
    });
  }
};
